// ============================================================================
// UTILITÁRIO: DocumentProcessor.js — v1.0 — Pipeline PDF-First (MC4)
// Blueprint M.E.D.B. — Parte 5: Conversão Client-Side de Evidências Fotográficas
//
// Fluxo: HEIC/JPEG pesado → JPEG comprimido → PDF A4 (<1.5 MB)
// Sem processamento no servidor. 100% no navegador do cliente.
// ============================================================================


const A4_WIDTH_PT  = 595.28;
const A4_HEIGHT_PT = 841.89;
const MAX_PDF_MB   = 1.5;
const PDF_IMAGE_QUALITY = 0.72;

const HEIC_MIME_TYPES = new Set(['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence']);
const IMAGE_MIME_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/bmp']);

function isHeic(file) {
  if (!file) return false;
  if (HEIC_MIME_TYPES.has(String(file.type || '').toLowerCase())) return true;
  const ext = String(file.name || '').toLowerCase().split('.').pop();
  return ext === 'heic' || ext === 'heif';
}

function isImage(file) {
  if (!file) return false;
  return IMAGE_MIME_TYPES.has(String(file.type || '').toLowerCase()) || isHeic(file);
}

async function heicToJpeg(blob) {
  try {
    const heic2any = (await import('heic2any')).default;
    const result = await heic2any({ blob, toType: 'image/jpeg', quality: 0.85 });
    return Array.isArray(result) ? result[0] : result;
  } catch (e) {
    throw new Error(`Falha na conversão HEIC→JPEG: ${e?.message || e}`);
  }
}

async function compressJpeg(blob, originalName) {
  const options = {
    maxSizeMB: MAX_PDF_MB,
    maxWidthOrHeight: 2048,
    useWebWorker: true,
    fileType: 'image/jpeg',
  };
  try {
    const imageCompression = (await import('browser-image-compression')).default;
    const file = blob instanceof File ? blob : new File([blob], originalName || 'image.jpg', { type: 'image/jpeg' });
    return await imageCompression(file, options);
  } catch {
    return blob;
  }
}

async function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result));
    reader.onerror  = () => reject(new Error('Falha ao ler blob como base64'));
    reader.readAsDataURL(blob);
  });
}

async function wrapInPdf(base64Data, fileName) {
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4', compress: true });
  const margin = 20;
  const maxW   = A4_WIDTH_PT  - margin * 2;
  const maxH   = A4_HEIGHT_PT - margin * 2;

  const img = new Image();
  img.src = base64Data;

  let imgW = img.naturalWidth  || maxW;
  let imgH = img.naturalHeight || maxH;
  const ratio = Math.min(maxW / imgW, maxH / imgH, 1);
  imgW *= ratio;
  imgH *= ratio;

  const x = margin + (maxW - imgW) / 2;
  const y = margin + (maxH - imgH) / 2;

  pdf.addImage(base64Data, 'JPEG', x, y, imgW, imgH, undefined, 'FAST');
  return pdf.output('blob');
}

export async function processDocumentForUpload(file) {
  if (!file) return file;

  if (!isImage(file)) {
    return file;
  }

  try {
    let workingBlob = file;

    if (isHeic(file)) {
      workingBlob = await heicToJpeg(file);
    }

    const compressed = await compressJpeg(workingBlob, file.name);

    const base64 = await blobToBase64(compressed);

    const pdfBlob = await wrapInPdf(base64, file.name);

    const safeName = String(file.name || 'documento')
      .replace(/\.(heic|heif|jpg|jpeg|png|webp|bmp)$/i, '')
      + '.pdf';

    return new File([pdfBlob], safeName, { type: 'application/pdf' });

  } catch (err) {
    console.warn('[DocumentProcessor] Falha no pipeline PDF-First — retornando arquivo original:', err?.message || err);
    return file;
  }
}

export function shouldProcess(file) {
  if (!file) return false;
  if (isHeic(file)) return true;
  if (IMAGE_MIME_TYPES.has(String(file.type || '').toLowerCase())) {
    return file.size > 1.5 * 1024 * 1024;
  }
  return false;
}
